import { Aplication } from '../types/aplication.type';

type ParsedRole = {
    roadId: string;
    isAdmin: boolean;
    impersonate: { read: boolean; write: boolean; viewSessions: boolean };
};

/**
 * Derives a human-readable display name for a roadId when no i18n override exists.
 * Strips the 'PRISM_' prefix, hyphenates the letters/digits boundary, and uppercases.
 * Example: 'PRISM_I66' -> 'I-66'.
 */
export function deriveAplicationDisplayName(roadId: string): string {
    const withoutPrefix = roadId.replace(/^PRISM_/, '');
    const hyphenated = withoutPrefix.replace(/^([A-Za-z]+)(\d+)$/, '$1-$2');

    return hyphenated.toUpperCase();
}

/**
 * Parses raw Entra ID role strings into distinct Aplication entries, one per roadId.
 * Recognized role shapes: 'Road.Admin.{roadId}', 'Impersonate.Read.{roadId}', 'Impersonate.Write.{roadId}', 'Impersonate.ViewSesions.{roadId}'.
 * Malformed or unrecognized roles are ignored without throwing. Output order is stable
 * (first-appearance order for the given input).
 */
export function parseAplications(roles: string[]): Aplication[] {
    const aplications = new Map<string, Aplication>();

    for (const role of roles) {
        const parsed = parseRole(role);

        if (!parsed) {
            continue;
        }

        const existing = aplications.get(parsed.roadId);

        if (existing) {
            existing.admin = existing.admin || parsed.isAdmin;
            existing.impersonate.read = existing.impersonate.read || parsed.impersonate.read;
            existing.impersonate.write = existing.impersonate.write || parsed.impersonate.write;
            existing.impersonate.viewSessions = existing.impersonate.viewSessions || parsed.impersonate.viewSessions;
            continue;
        }

        aplications.set(parsed.roadId, {
            roadId: parsed.roadId,
            displayName: deriveAplicationDisplayName(parsed.roadId),
            admin: parsed.isAdmin,
            impersonate: parsed.impersonate,
        });
    }

    return Array.from(aplications.values());
}

function parseRole(role: string): ParsedRole | null {
    const segments = role.split('.');

    if (segments.length !== 3) {
        return null;
    }

    const [scope, action, roadId] = segments;

    if (!roadId) {
        return null;
    }

    if (scope === 'Road' && action === 'Admin') {
        return { roadId, isAdmin: true, impersonate: { read: false, write: false, viewSessions: false } };
    }

    if (scope === 'Impersonate' && action === 'Read') {
        return { roadId, isAdmin: false, impersonate: { read: true, write: false, viewSessions: false } };
    }

    if (scope === 'Impersonate' && action === 'Write') {
        return { roadId, isAdmin: false, impersonate: { read: false, write: true, viewSessions: false } };
    }

    if (scope === 'Impersonate' && action === 'ViewSesions') {
        return { roadId, isAdmin: false, impersonate: { read: false, write: false, viewSessions: true } };
    }

    return null;
}
